import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';
import { TranslatePipe } from '@ngx-translate/core';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { useBreakpoint } from '../../../../shared/composables/useBreakpoint';
import { CourseRatingComponent } from '../../../courses/components/course-rating/course-rating.component';
import { CourseVideoComponent } from '../../../courses/components/course-video/course-video.component';
import { QuizService } from '../../../courses/services/quiz.service';
import { ParentLearnEnrollCardComponent } from '../../components/parent-learn-enroll-card/parent-learn-enroll-card.component';
import { ParentLearnLessonListComponent } from '../../components/parent-learn-lesson-list/parent-learn-lesson-list.component';
import { ParentLearnProgressComponent } from '../../components/parent-learn-progress/parent-learn-progress.component';
import { ParentLearnQuizComponent } from '../../components/parent-learn-quiz/parent-learn-quiz.component';
import { ParentLearnSkeletonComponent } from '../../components/parent-learn-skeleton/parent-learn-skeleton.component';
import { ParentLearnStickyPlayerComponent } from '../../components/parent-learn-sticky-player/parent-learn-sticky-player.component';
import {
  ParentLearnPhase,
  ParentLessonItem,
  ParentQuizResult,
} from '../../models/parent-learning.types';
import {
  PARENT_LEARNING_QUERY_KEYS,
  ParentLearningService,
} from '../../services/parent-learning.service';

@Component({
  selector: 'app-parent-course-learn',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ParentButtonComponent,
    CourseVideoComponent,
    CourseRatingComponent,
    ParentLearnProgressComponent,
    ParentLearnLessonListComponent,
    ParentLearnEnrollCardComponent,
    ParentLearnQuizComponent,
    ParentLearnSkeletonComponent,
    ParentLearnStickyPlayerComponent,
  ],
  templateUrl: './parent-course-learn.component.html',
  styleUrl: './parent-course-learn.component.scss',
  host: {
    '[class.parent-learn-host--lesson-active]': 'isLessonActive()',
    '[class.parent-learn-host--mobile]': 'isMobile()',
    '[class.parent-learn-host--video-docked]': 'videoDocked()',
  },
})
export class ParentCourseLearnComponent implements OnInit {
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #learning = inject(ParentLearningService);
  #quizService = inject(QuizService);
  #breakpoint = useBreakpoint();
  #destroyRef = inject(DestroyRef);
  #videoObserver: IntersectionObserver | null = null;

  readonly stageRef = viewChild<ElementRef<HTMLElement>>('learnStage');
  readonly stickySentinelRef = viewChild<ElementRef<HTMLElement>>('stickySentinel');
  readonly dockRef = viewChild<ElementRef<HTMLElement>>('nowPlayingDock');
  readonly dockAnchorRef = viewChild<ElementRef<HTMLElement>>('dockAnchor');

  readonly courseId = signal<string | null>(null);
  readonly selectedLessonId = signal<string | null>(null);
  readonly pendingLessonId = signal<string | null>(null);
  readonly phase = signal<ParentLearnPhase>('overview');
  readonly reviewMode = signal(false);
  readonly videoWatched = signal(false);
  readonly quizResult = signal<ParentQuizResult | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly showRewatch = signal(false);
  readonly newlyUnlockedId = signal<string | null>(null);
  readonly videoDocked = signal(false);
  readonly dockedVideoHeight = signal(0);
  readonly descriptionExpanded = signal(false);

  readonly isMobile = computed(
    () => this.#breakpoint.currentBreakpoint() === 'XSmall',
  );

  readonly courseDetailQuery = injectQuery(() => ({
    queryKey: PARENT_LEARNING_QUERY_KEYS.detail(this.courseId() ?? ''),
    enabled: !!this.courseId(),
    queryFn: () => this.#learning.fetchCourseDetail(this.courseId()!),
  }));

  readonly enrollmentQuery = injectQuery(() => ({
    queryKey: PARENT_LEARNING_QUERY_KEYS.enrollment(this.courseId() ?? ''),
    enabled: !!this.courseId(),
    staleTime: 60_000,
    queryFn: () => this.#learning.fetchEnrollment(this.courseId()!),
  }));

  readonly taskQuery = injectQuery(() => ({
    queryKey: PARENT_LEARNING_QUERY_KEYS.task(this.selectedLessonId() ?? ''),
    enabled:
      !!this.selectedLessonId() &&
      this.isJoined() &&
      this.phase() !== 'overview',
    queryFn: () => this.#learning.fetchTaskPlain(this.selectedLessonId()!),
  }));

  readonly quizQuery = injectQuery(() => ({
    queryKey: PARENT_LEARNING_QUERY_KEYS.quiz(this.selectedLessonId() ?? ''),
    enabled:
      !!this.selectedLessonId() &&
      this.isJoined() &&
      this.phase() !== 'overview',
    queryFn: () => this.#learning.fetchQuizByTaskId(this.selectedLessonId()!),
  }));

  readonly joinMutation = injectMutation(() => ({
    mutationFn: (courseId: string) => this.#learning.joinCourse(courseId),
    onSuccess: () => {
      const id = this.courseId();
      if (id) {
        this.#learning.invalidateCourse(id);
      }
    },
  }));

  readonly leaveMutation = injectMutation(() => ({
    mutationFn: (courseId: string) => this.#learning.leaveCourse(courseId),
    onSuccess: () => {
      const id = this.courseId();
      if (id) {
        this.#learning.invalidateCourse(id);
      }
      this.resetLessonFlow();
    },
  }));

  readonly rateMutation = injectMutation(() => ({
    mutationFn: (rating: number) =>
      this.#learning.rateCourse(this.courseId()!, rating),
    onSuccess: () => {
      const id = this.courseId();
      if (id) {
        this.#learning.invalidateCourse(id);
      }
    },
  }));

  readonly detail = computed(() => this.courseDetailQuery.data());
  readonly enrollment = computed(() => this.enrollmentQuery.data());
  readonly lessons = computed((): ParentLessonItem[] => this.detail()?.tasks ?? []);
  readonly isLoading = computed(
    () =>
      this.courseDetailQuery.isLoading() || this.enrollmentQuery.isLoading(),
  );
  readonly loadError = computed(
    () =>
      (this.courseDetailQuery.error() as Error | null)?.message ??
      (this.enrollmentQuery.error() as Error | null)?.message ??
      null,
  );

  readonly isJoined = computed(
    () => this.enrollment()?.courseJoined === true,
  );
  readonly isCourseCompleted = computed(() => {
    const detail = this.detail();
    const enrollment = this.enrollment();
    return (
      detail?.completionPercent === 100 ||
      enrollment?.courseCompleted === true
    );
  });

  readonly userRating = computed(
    () => this.enrollment()?.course?.userRating ?? 0,
  );

  readonly isLessonActive = computed(
    () =>
      this.isJoined() &&
      this.phase() !== 'overview' &&
      this.phase() !== 'course-done',
  );

  readonly showStickyVideo = computed(
    () =>
      this.isMobile() &&
      this.phase() === 'watch' &&
      this.hasVideo() &&
      !this.taskQuery.isLoading(),
  );

  readonly selectedLesson = computed(() => {
    const id = this.selectedLessonId();
    return this.lessons().find((lesson) => lesson.id === id) ?? null;
  });

  readonly activeTask = computed(() => this.taskQuery.data() ?? null);

  readonly activeTaskDescription = computed(
    () => this.activeTask()?.description ?? '',
  );

  readonly videoUrl = computed(
    () => (this.activeTask() as { contentUrl?: string } | null)?.contentUrl ?? '',
  );

  readonly posterUrl = computed(() => {
    const task = this.activeTask();
    const courseImg = this.detail()?.courseImageUrl ?? '';
    if (!task) {
      return courseImg;
    }
    return (
      (task as { thumbnailUrl?: string }).thumbnailUrl ||
      (task as { coverUrl?: string }).coverUrl ||
      courseImg
    );
  });

  readonly quizId = computed(() => this.quizQuery.data()?.id ?? '');
  readonly hasQuiz = computed(() => !!this.quizId().trim());
  readonly hasVideo = computed(() => !!this.videoUrl().trim());

  readonly continueLesson = computed(() => {
    if (!this.isJoined()) {
      return null;
    }
    return (
      this.lessons().find((lesson) => !lesson.isLocked && !lesson.isCompleted) ??
      null
    );
  });

  readonly nextLesson = computed(() => {
    const current = this.selectedLesson();
    const sorted = [...this.lessons()].sort(
      (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
    );
    if (!current) {
      return null;
    }
    const idx = sorted.findIndex((lesson) => lesson.id === current.id);
    if (idx < 0) {
      return null;
    }
    return (
      sorted.slice(idx + 1).find((lesson) => !lesson.isLocked) ?? null
    );
  });

  ngOnInit(): void {
    const id = this.#route.snapshot.paramMap.get('id');
    this.courseId.set(id);
    this.descriptionExpanded.set(false);
    const lessonParam = this.#route.snapshot.queryParamMap.get('lesson');
    if (lessonParam) {
      this.pendingLessonId.set(lessonParam);
    }
    if (!id) {
      void this.#router.navigate(['/home/courses']);
    }
  }

  constructor() {
    effect(
      () => {
        const detail = this.detail();
        if (
          detail &&
          detail.completionPercent === 100 &&
          this.phase() === 'overview'
        ) {
          this.phase.set('course-done');
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const pendingId = this.pendingLessonId();
        const lessons = this.lessons();
        if (!pendingId || !lessons.length || !this.isJoined()) {
          return;
        }
        const lesson = lessons.find((item) => item.id === pendingId);
        if (lesson && !lesson.isLocked) {
          this.pendingLessonId.set(null);
          this.openLesson(lesson);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const task = this.activeTask();
        const lesson = this.selectedLesson();
        if (!task || !lesson || lesson.isCompleted || this.phase() !== 'watch') {
          return;
        }
        if (this.hasVideo() || this.quizQuery.isPending()) {
          return;
        }
        if (this.hasQuiz()) {
          this.phase.set('quiz');
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.phase() !== 'quiz') {
          return;
        }
        const next = this.nextLesson();
        if (next) {
          this.#learning.prefetchLesson(next.id);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.phase() === 'quiz') {
          requestAnimationFrame(() => {
            const quizHost = document.querySelector(
              '.parent-learn-quiz',
            ) as HTMLElement | null;
            quizHost?.focus();
          });
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      this.showStickyVideo();
      this.stickySentinelRef();
      this.#syncVideoDockObserver();
    });

    effect(() => {
      const shouldPortal =
        this.isMobile() && this.videoDocked() && !!this.selectedLesson();
      this.dockRef();
      queueMicrotask(() => this.#syncDockPortal(shouldPortal));
    });

    this.#destroyRef.onDestroy(() => {
      this.#videoObserver?.disconnect();
      this.#videoObserver = null;
      this.#restoreDockFromBody();
    });
  }

  startQuiz(): void {
    this.showRewatch.set(false);
    this.phase.set('quiz');
    this.syncLessonQueryParam();
    this.scrollToStage();
  }

  backToVideo(): void {
    this.showRewatch.set(false);
    this.phase.set('watch');
    this.syncLessonQueryParam();
    this.scrollToStage();
  }

  onRewatchVideo(): void {
    this.showRewatch.set(false);
    this.videoWatched.set(false);
    this.quizResult.set(null);
    this.phase.set('watch');
    this.syncLessonQueryParam();
    this.scrollToStage();
  }

  onQuizIncorrect(): void {
    if (this.hasVideo()) {
      this.showRewatch.set(true);
    }
  }

  taskErrorMessage(): string {
    const err = this.taskQuery.error();
    if (err instanceof Error) {
      return err.message;
    }
    return 'Failed to load lesson';
  }

  isDescriptionLong(description: string): boolean {
    return description.trim().length > 120;
  }

  toggleDescription(): void {
    this.descriptionExpanded.update((expanded) => !expanded);
  }

  goBack(): void {
    if (this.phase() === 'overview' || this.phase() === 'course-done') {
      void this.#router.navigate(['/home/courses']);
      return;
    }
    this.resetLessonFlow();
  }

  onJoin(): void {
    const id = this.courseId();
    if (id) {
      this.joinMutation.mutate(id);
    }
  }

  onLeave(): void {
    const id = this.courseId();
    if (id) {
      this.leaveMutation.mutate(id);
    }
  }

  onRateCourse(rating: number): void {
    this.rateMutation.mutate(rating);
  }

  onContinueLearning(): void {
    const lesson = this.continueLesson();
    if (lesson) {
      this.openLesson(lesson);
    }
  }

  onLessonSelect(lessonId: string): void {
    const lesson = this.lessons().find((item) => item.id === lessonId);
    if (lesson) {
      this.openLesson(lesson);
    }
  }

  openLesson(lesson: ParentLessonItem): void {
    if (!this.isJoined() || lesson.isLocked) {
      return;
    }

    this.actionError.set(null);
    this.showRewatch.set(false);
    this.videoDocked.set(false);
    this.dockedVideoHeight.set(0);
    this.selectedLessonId.set(lesson.id);
    this.quizResult.set(null);
    this.syncLessonQueryParam(lesson.id);

    if (lesson.isCompleted) {
      this.reviewMode.set(true);
      this.videoWatched.set(true);
      this.phase.set('watch');
    } else {
      this.reviewMode.set(false);
      this.videoWatched.set(false);
      this.phase.set(lesson.hasVideo ? 'watch' : 'watch');
    }

    if (!this.isMobile()) {
      this.scrollToStage();
    }
  }

  onVideoEnded(): void {
    if (this.reviewMode()) {
      return;
    }
    this.videoWatched.set(true);
    void this.handleVideoCompleted();
  }

  async handleVideoCompleted(): Promise<void> {
    if (this.quizQuery.isPending()) {
      await this.quizQuery.refetch();
    }
    if (this.hasQuiz()) {
      this.phase.set('quiz');
      this.syncLessonQueryParam();
      this.scrollToStage();
      return;
    }
    await this.completeLessonWithoutQuiz();
  }

  async completeLessonWithoutQuiz(): Promise<void> {
    const taskId = this.selectedLessonId();
    if (!taskId || this.reviewMode()) {
      return;
    }
    this.actionError.set(null);
    try {
      try {
        await this.#quizService.startTask(taskId);
      } catch (err) {
        if (
          !(err instanceof Error) ||
          !err.message?.includes('already completed')
        ) {
          throw err;
        }
      }
      await this.#quizService.completeTaskWithDetails(taskId, {
        quizCorrectAnswers: 0,
        quizTotalQuestions: 0,
        isPerfectQuiz: false,
      });
      this.onQuizCompleted({
        correctCount: 0,
        total: 0,
        isPerfect: false,
      });
    } catch (err) {
      this.actionError.set(
        err instanceof Error ? err.message : 'Failed to complete lesson',
      );
    }
  }

  onQuizCompleted(result: ParentQuizResult): void {
    this.quizResult.set(result);
    if (result.isPerfect) {
      this.triggerPerfectHaptic();
    }

    if (this.reviewMode()) {
      return;
    }

    void this.finishLesson(result);
  }

  async finishLesson(_result: ParentQuizResult): Promise<void> {
    const courseId = this.courseId();
    if (courseId) {
      this.#learning.invalidateCourse(courseId);
    }
    const lessonId = this.selectedLessonId();
    if (lessonId) {
      this.#learning.invalidateTask(lessonId);
    }

    await Promise.all([
      this.courseDetailQuery.refetch(),
      this.enrollmentQuery.refetch(),
    ]);

    const detail = this.detail();
    const enrollment = this.enrollment();
    const courseComplete =
      detail?.completionPercent === 100 ||
      enrollment?.courseCompleted === true;

    if (courseComplete) {
      this.phase.set('course-done');
      this.clearLessonQueryParam();
    } else {
      this.phase.set('lesson-done');
      const unlocked = this.nextLesson();
      if (unlocked) {
        this.newlyUnlockedId.set(unlocked.id);
        setTimeout(() => this.newlyUnlockedId.set(null), 900);
      }
    }
    this.scrollToStage();
  }

  onNextLesson(): void {
    const next = this.nextLesson();
    if (next) {
      this.openLesson(next);
      return;
    }
    if (this.isCourseCompleted()) {
      this.phase.set('course-done');
      this.clearLessonQueryParam();
    } else {
      this.resetLessonFlow();
    }
  }

  onBackToOverview(): void {
    this.resetLessonFlow();
  }

  resetLessonFlow(): void {
    this.selectedLessonId.set(null);
    this.reviewMode.set(false);
    this.videoWatched.set(false);
    this.quizResult.set(null);
    this.showRewatch.set(false);
    this.videoDocked.set(false);
    this.dockedVideoHeight.set(0);
    this.actionError.set(null);
    this.phase.set(this.isCourseCompleted() ? 'course-done' : 'overview');
    this.clearLessonQueryParam();
  }

  canStartQuiz(): boolean {
    if (!this.hasQuiz()) {
      return false;
    }
    if (this.reviewMode()) {
      return true;
    }
    if (!this.hasVideo()) {
      return true;
    }
    return this.videoWatched();
  }

  private syncLessonQueryParam(lessonId?: string): void {
    const id = lessonId ?? this.selectedLessonId();
    void this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { lesson: id ?? null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private clearLessonQueryParam(): void {
    void this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { lesson: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private triggerPerfectHaptic(): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate([30, 40, 30]);
    }
  }

  scrollToInlineVideo(): void {
    this.stickySentinelRef()?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  #syncDockPortal(shouldPortal: boolean): void {
    const dock = this.dockRef()?.nativeElement;
    const anchor = this.dockAnchorRef()?.nativeElement;
    if (!dock || !anchor) {
      return;
    }

    if (shouldPortal && dock.parentElement !== document.body) {
      document.body.appendChild(dock);
      document.body.classList.add('parent-learn-dock-open');
      return;
    }

    if (!shouldPortal && dock.parentElement === document.body) {
      anchor.appendChild(dock);
      document.body.classList.remove('parent-learn-dock-open');
    }
  }

  #restoreDockFromBody(): void {
    const dock = this.dockRef()?.nativeElement;
    const anchor = this.dockAnchorRef()?.nativeElement;
    if (dock?.parentElement === document.body && anchor) {
      anchor.appendChild(dock);
    }
    document.body.classList.remove('parent-learn-dock-open');
  }

  #findScrollRoot(element: HTMLElement): Element | null {
    let parent = element.parentElement;
    while (parent) {
      const { overflowY } = getComputedStyle(parent);
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  #syncVideoDockObserver(): void {
    this.#videoObserver?.disconnect();
    this.#videoObserver = null;

    if (!this.showStickyVideo()) {
      this.videoDocked.set(false);
    this.dockedVideoHeight.set(0);
      return;
    }

    const sentinel = this.stickySentinelRef()?.nativeElement;
    if (!sentinel) {
      return;
    }

    const root = this.#findScrollRoot(sentinel);

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        if (!entry.isIntersecting) {
          const slot = sentinel.nextElementSibling as HTMLElement | null;
          const height =
            slot
              ?.querySelector('.parent-learn__video')
              ?.getBoundingClientRect().height ?? 0;
          this.dockedVideoHeight.set(height > 0 ? Math.round(height) : 200);
        }

        this.videoDocked.set(!entry.isIntersecting);
      },
      { threshold: [0, 1], root },
    );

    this.#videoObserver.observe(sentinel);
  }

  private scrollToStage(): void {
    if (this.isMobile() && this.isLessonActive()) {
      return;
    }
    requestAnimationFrame(() => {
      this.stageRef()?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }
}
